import { OCI_LOGGING_CONFIG } from '../config';

export enum TelemetryEvent {
    API_CALL = 'api_call',
    USER_ACTION = 'user_action',
    ERROR = 'error',
    PERFORMANCE = 'performance_metric',
    AUTH = 'authentication_event'
}

export interface TelemetryData {
    event: TelemetryEvent;
    timestamp: string;
    properties: Record<string, any>;
    userId?: string;
    sessionId?: string;
}

/**
 * Interface for platform-specific telemetry providers (e.g., OpenTelemetry)
 */
export interface ITelemetryProvider {
    track: (event: TelemetryEvent, propertiesRecord: Record<string, any>, userId?: string) => void;
    flush: () => void;
}

export class TelemetryService {
    private static instance: TelemetryService;
    private events: TelemetryData[] = [];
    private maxBatchSize: number = 50;
    private provider: ITelemetryProvider | null = null;

    private constructor() {}

    public static getInstance(): TelemetryService {
        if (!TelemetryService.instance) {
            TelemetryService.instance = new TelemetryService();
        }
        return TelemetryService.instance;
    }

    /**
     * Register a platform-specific provider (e.g., initialized OpenTelemetry)
     */
    public registerProvider(provider: ITelemetryProvider) {
        this.provider = provider;
    }

    public track(event: TelemetryEvent, properties: Record<string, any> = {}, userId?: string) {
        // 1. Generic internal tracking
        const data: TelemetryData = {
            event,
            timestamp: new Date().toISOString(),
            properties,
            userId,
            sessionId: this.getSessionId()
        };

        this.events.push(data);

        // 2. Delegate to registered provider if available
        if (this.provider) {
            try {
                this.provider.track(event, properties, userId);
            } catch (e) {
                // Silently ignore provider errors
            }
        }

        if (this.events.length >= this.maxBatchSize) {
            this.flush();
        }
    }

    private getSessionId(): string {
        if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
            let sid = sessionStorage.getItem('SESSION_ID');
            if (!sid) {
                sid = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                sessionStorage.setItem('SESSION_ID', sid);
            }
            return sid;
        }
        return 'node_session';
    }

    public flush() {
        if (this.provider) {
            try {
                this.provider.flush();
            } catch {}
        }
        if (this.events.length === 0) return;
        this.events = [];
    }
}

export const telemetry = TelemetryService.getInstance();
